import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';

export interface SshHost {
  id: string;
  userId: string;
  name: string;
  hostname: string;
  port: number;
  sshUser: string;
  authMethod: 'key' | 'agent' | 'password';
  bastionHostId: string | null;
  hostKeyFingerprint: string | null;
  claudeBinaryPath: string;
  workingDirectory: string | null;
  envVars: Record<string, string> | null;
  maxConcurrentInstances: number;
  connectionPoolSize: number;
  keepaliveIntervalMs: number;
  status: 'connected' | 'disconnected' | 'error' | 'testing';
  lastError: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateSshHostDto {
  name: string;
  hostname: string;
  port?: number;
  sshUser: string;
  authMethod?: string;
  privateKey?: string;
  passphrase?: string;
  bastionHostId?: string;
  claudeBinaryPath?: string;
  workingDirectory?: string;
  envVars?: Record<string, string>;
  maxConcurrentInstances?: number;
  connectionPoolSize?: number;
}

export interface SshHostStats {
  activeInstances: number;
  totalInstances: number;
  maxConcurrentInstances: number;
  connectionPoolSize: number;
  status: string;
}

export function useSshHosts() {
  return useQuery<SshHost[]>({
    queryKey: ['ssh-hosts'],
    queryFn: async () => {
      const res = await api.get('/api/ssh-hosts');
      return res.data;
    },
    refetchInterval: 15000,
  });
}

export function useSshHost(id: string | undefined) {
  return useQuery<SshHost>({
    queryKey: ['ssh-hosts', id],
    queryFn: async () => {
      const res = await api.get(`/api/ssh-hosts/${id}`);
      return res.data;
    },
    enabled: !!id,
    refetchInterval: 10000,
  });
}

export function useCreateSshHost() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateSshHostDto) => {
      const res = await api.post('/api/ssh-hosts', data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ssh-hosts'] });
    },
  });
}

export function useUpdateSshHost() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      ...data
    }: Partial<CreateSshHostDto> & { id: string }) => {
      const res = await api.patch(`/api/ssh-hosts/${id}`, data);
      return res.data;
    },
    onSuccess: (data: SshHost) => {
      queryClient.invalidateQueries({ queryKey: ['ssh-hosts', data.id] });
      queryClient.invalidateQueries({ queryKey: ['ssh-hosts'] });
    },
  });
}

export function useDeleteSshHost() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/ssh-hosts/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ssh-hosts'] });
    },
  });
}

export function useTestSshHost() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post(`/api/ssh-hosts/${id}/test`);
      return res.data as { success: boolean; message: string; claudeVersion?: string };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ssh-hosts'] });
    },
  });
}

export function useInstallClaude() {
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post(`/api/ssh-hosts/${id}/install`);
      return res.data as { success: boolean; output: string };
    },
  });
}

export interface SshSetupCommand {
  token: string;
  setupCommand: string;
  expiresAt: string;
}

export function useGenerateSshSetupCommand() {
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post(`/api/ssh-hosts/${id}/setup-command`);
      return res.data as SshSetupCommand;
    },
  });
}

export function useSshHostStats(id: string | undefined) {
  return useQuery<SshHostStats>({
    queryKey: ['ssh-hosts', id, 'stats'],
    queryFn: async () => {
      const res = await api.get(`/api/ssh-hosts/${id}/stats`);
      return res.data;
    },
    enabled: !!id,
    refetchInterval: 10000,
  });
}
